import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
    selectedPrescripteursSelector,
    SousClient,
    SousClientApi,
    SousClientsSelector,
    useSociete,
    useTiersServiceSelector,
} from '@europrocurement/l2d-domain';
import { faBuildingUser } from '@fortawesome/sharp-regular-svg-icons';
import {
    ContinueButton,
    FlexyTextField,
    SelectSocieteCard,
    useL2DTheme,
    useModal,
} from '@europrocurement/flexy-components';
import SelectSociete from './SelectSociete';

export type SelectSocieteWrapperProps = {
    onSelect: (
        ids: (number | undefined)[],
        sousClients: SousClientApi[],
        sousClientsOpere: SousClientApi[],
        referenceClient?: string,
    ) => void;
    title?: string;
    submitButtonLabel?: string;
    displayOpere?: boolean;
    defaultOpere?: number;
    defaultFacturee?: number;
    idFormalites: Array<number>;
    isSimplePath?: boolean;
};

const SelectSocieteWrapper: React.FunctionComponent<SelectSocieteWrapperProps> = function ({
    onSelect,
    title = 'Pour quelle société est destinée la formalité ?',
    submitButtonLabel = 'Continuer',
    displayOpere = true,
    defaultOpere,
    defaultFacturee,
    idFormalites,
    isSimplePath = false,
}) {
    const datasource = useTiersServiceSelector(SousClientsSelector).main;
    const prescripteur = useTiersServiceSelector(selectedPrescripteursSelector);
    const societeId = useSociete();
    const { palette } = useL2DTheme();
    const { modalActions } = useModal();
    const [operatedClient, setOperatedClient] = useState<SousClientApi | undefined>();
    const [billedClient, setBilledClient] = useState<SousClientApi | undefined>();
    const [referenceClient, setReferenceClient] = useState('');
    const [referenceClientError, setReferenceClientError] = useState('');
    const [, setSousClients] = useState<SousClient[]>([]);

    useEffect(() => {
        // dafc[2] -> Facture | 1 = Prescripteur -  2 = Client
        if (
            prescripteur &&
            !societeId &&
            prescripteur?.relationsSocietes[0].dafc &&
            prescripteur.relationsSocietes[0].dafc[2] === '1'
        ) {
            // cas société par défaut
            setBilledClient(prescripteur);
        } else if (prescripteur && societeId && prescripteur.relationsSocietes) {
            // cas d'une autre société selectionnée
            const relatedCompany = prescripteur.relationsSocietes.find(
                (relation) => relation.societe?.id === societeId,
            );
            if (relatedCompany?.dafc && relatedCompany.dafc[2] === '1') {
                setBilledClient(prescripteur);
            }
        }
    }, [prescripteur, societeId]);

    // get tiers/client related to the collection
    useEffect(() => {
        if (datasource.status === 'succeeded') {
            setSousClients(datasource.data);
            if (defaultOpere) {
                const opere = datasource.data.find((sousClient) => defaultOpere === sousClient.id);
                setOperatedClient(opere);
            }
            if (defaultFacturee) {
                const facture = datasource.data.find(
                    (sousClient) => Number(defaultFacturee) === sousClient.id,
                );
                setBilledClient(facture);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datasource.status]);

    const handleUpdateClientBilled = () => {
        modalActions.call(
            <SelectSociete
                title="Pour quelle société facturée est destinée la commande ?"
                onSelect={(ids, sousClientSelect) => {
                    if (sousClientSelect.length === 1) {
                        setBilledClient(sousClientSelect[0]);
                        if (!displayOpere) {
                            onSelect([sousClientSelect[0].id], [sousClientSelect[0]], []);
                        }
                    }
                    modalActions.close();
                }}
                idFormalites={idFormalites}
            />,
        );
    };

    const handleUpdateClientOpere = () => {
        modalActions.call(
            <SelectSociete
                title="Pour quelle société opérée est destinée la commande ?"
                onSelect={(ids, sousClientSelect) => {
                    if (sousClientSelect.length === 1) {
                        setOperatedClient(sousClientSelect[0]);

                        if (!billedClient) {
                            setBilledClient(sousClientSelect[0]);
                        }
                    }
                    modalActions.close();
                }}
                idFormalites={idFormalites}
                isSimplePath={!operatedClient && isSimplePath}
            />,
        );
    };

    const handleSubmit = useCallback(() => {
        if (onSelect && billedClient && operatedClient) {
            onSelect(
                [billedClient.id],
                [billedClient],
                [operatedClient],
                referenceClient || undefined,
            );
        }
    }, [onSelect, billedClient, operatedClient, referenceClient]);

    useEffect(() => {
        if (isSimplePath) {
            handleUpdateClientOpere();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box>
            <Box
                sx={{
                    width: '100%',
                }}
            >
                {title !== '' && (
                    <Typography
                        className="title"
                        variant="l2dh4"
                        sx={{
                            color: palette.text.primary,
                        }}
                    >
                        {title}
                    </Typography>
                )}
                {displayOpere && (
                    <SelectSocieteCard
                        title="Société opérée"
                        company={
                            operatedClient
                                ? {
                                      name: operatedClient.raisonSociale,
                                      identifier: operatedClient?.siren as string,
                                  }
                                : undefined
                        }
                        icon={faBuildingUser}
                        onClick={handleUpdateClientOpere}
                    />
                )}

                {/* billedClient society info and edit action */}
                <SelectSocieteCard
                    title="Société facturée"
                    company={
                        billedClient
                            ? {
                                  name: billedClient.raisonSociale,
                                  identifier: billedClient?.siren as string,
                              }
                            : undefined
                    }
                    icon={faBuildingUser}
                    onClick={handleUpdateClientBilled}
                />

                {displayOpere && (
                    <Box sx={{ mt: 2 }}>
                        <FlexyTextField
                            variant="outlined"
                            fullWidth
                            label="Référence client"
                            placeholder="Non renseignée"
                            value={referenceClient}
                            onChange={(e) => {
                                const val = e.target.value;
                                setReferenceClient(val);
                                setReferenceClientError(
                                    val.length > 50
                                        ? 'La référence ne peut pas dépasser 50 caractères'
                                        : '',
                                );
                            }}
                            error={!!referenceClientError}
                            helperText={referenceClientError}
                        />
                        <Typography
                            variant="l2dbodyxs"
                            sx={{
                                color: 'text.secondary',
                                fontStyle: 'italic',
                                mt: 0.5,
                                display: 'block',
                            }}
                        >
                            Facultatif — saisissez une référence pour retrouver facilement cette
                            formalité.
                        </Typography>
                    </Box>
                )}
            </Box>
            {displayOpere && (
                <Box
                    className="actions"
                    sx={{
                        width: '100%',
                        marginTop: '30px',
                    }}
                >
                    <ContinueButton
                        onClick={handleSubmit}
                        disabled={!billedClient || !operatedClient || !!referenceClientError}
                        libelle={submitButtonLabel}
                    />
                </Box>
            )}
        </Box>
    );
};

export default SelectSocieteWrapper;
